import type {
  PlatformUsageSummary,
  SetStorageQuotaInput,
  StorageQuota,
  UsageSummary,
} from "@lumora/bridge";
import { formatUsd } from "@lumora/core";
import {
  estimateTrafficCost,
  formatBytesGB,
  platformShare,
  STARTER_INCLUDED_REQUESTS,
  TRAFFIC_CENTS_PER_1K,
} from "./siteTrafficMetrics.js";
import { StorageQuotaForm } from "./StorageQuotaForm.js";
import { tenantRequestCount } from "./siteManageCapabilities.js";
import { TrafficDailyBars } from "./TrafficDailyBars.js";

export type SiteTrafficPanelProps = {
  tenantId: string;
  platform: PlatformUsageSummary | null;
  siteUsage: UsageSummary | null;
  storageQuota?: StorageQuota | null;
  onSaveStorageQuota?: (input: SetStorageQuotaInput) => void | Promise<void>;
};

function ShareBar({ site, total }: { site: number; total: number }) {
  const pct = platformShare(site, total) * 100;
  return (
    <div className="platform-traffic-share" aria-label={`${pct.toFixed(1)} percent of platform traffic`}>
      <div className="platform-traffic-share-track">
        <div className="platform-traffic-share-fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="muted platform-traffic-share-label">
        {pct.toFixed(1)}% of platform API traffic this month
      </p>
    </div>
  );
}

/**
 * Clean traffic summary: requests, cost estimate, daily bars, platform share.
 * Closes #454.
 */
export function SiteTrafficPanel({
  tenantId,
  platform,
  siteUsage,
  storageQuota,
  onSaveStorageQuota,
}: SiteTrafficPanelProps) {
  const fromPlatform = tenantRequestCount(platform?.byTenant, tenantId);
  const requests = siteUsage?.totalRequests ?? fromPlatform ?? 0;
  const cost = estimateTrafficCost(requests);
  const periodStart = siteUsage?.periodStart ?? platform?.periodStart;
  const periodEnd = siteUsage?.periodEnd ?? platform?.periodEnd;
  const storage = siteUsage?.storage;
  const platformTotal = platform?.totalRequests ?? 0;

  const periodLabel =
    periodStart && periodEnd
      ? `${new Date(periodStart).toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${new Date(periodEnd).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
      : "this month";

  return (
    <div className="platform-traffic" data-platform-site-traffic>
      <div className="platform-traffic-kpis">
        <div>
          <p className="platform-traffic-kpi-label">Requests</p>
          <p className="platform-traffic-kpi-value">{requests.toLocaleString()}</p>
        </div>
        <div>
          <p className="platform-traffic-kpi-label">Est. overage (Starter)</p>
          <p className="platform-traffic-kpi-value">{formatUsd(cost.starterOverageCents)}</p>
        </div>
        <div>
          <p className="platform-traffic-kpi-label">If all billed</p>
          <p className="platform-traffic-kpi-value">{formatUsd(cost.listRateCents)}</p>
        </div>
      </div>

      <p className="muted platform-traffic-period">{periodLabel}</p>

      <TrafficDailyBars byDay={siteUsage?.byDay ?? []} />
      <ShareBar site={requests} total={platformTotal} />

      {storage ? (
        <p className="platform-traffic-storage" data-platform-storage data-storage-status={storage.status}>
          Storage: {formatBytesGB(storage.usedBytes)} / {formatBytesGB(storage.quotaBytes)} ({storage.status},{" "}
          {storage.overagePolicy})
        </p>
      ) : null}
      {storageQuota && onSaveStorageQuota ? (
        <StorageQuotaForm quota={storageQuota} onSave={onSaveStorageQuota} />
      ) : null}

      <p className="muted platform-traffic-footnote">
        Rate ~{formatUsd(TRAFFIC_CENTS_PER_1K)}/1k req
        {cost.includedRemaining > 0
          ? ` · ~${cost.includedRemaining.toLocaleString()} left in Starter’s ${STARTER_INCLUDED_REQUESTS.toLocaleString()} included`
          : " · Starter included pool used"}
        . Indicative — plan allotment may differ.
      </p>
    </div>
  );
}
